import { textColorFields } from '../_shared/textColor.js';
import { playButtonField } from '../_shared/videoPlayButton.js';
import { animationFields } from '../_shared/universal.js';
/**
 * parallax-hero — a FROM-SCRATCH, 1:1 rebuild of Shrine Pro's "3D Parallax hero" section
 * (shrine-pro-ref/sections/parallax-hero.liquid). A stack of independently-moving layers — a section
 * background (image or uploaded video) with an adjustable overlay scrim, ONE content layer (highlight
 * heading + prefix/suffix + rich text + up to two buttons, freely positioned) and up to TEN image
 * layers — that drift / zoom / rotate at their own speed as the section scrolls, producing the depth
 * effect. Every layer's parallax is knob-driven (vertical & horizontal speed, zoom, rotation, stacking
 * index), exactly matching Shrine's `<parallax-hero>` element; the scroll math lives in the React-free
 * `parallaxInit` engine.
 *
 * Field-type mapping (Shopify → ours): inline_richtext→richtext (bold → the single title-highlight color),
 * richtext→richtext, image_picker→image, video→video (an UPLOADED clip — aquilixShop rule: media is
 * uploaded, never a raw URL, see spec 0020), url→link, color→color, range/number/select/checkbox unchanged,
 * header→group, visible_if→visibleWhen. Ids are clean camelCase; labels / options / defaults stay faithful
 * to Shrine (the 6 color schemes + custom palette, the content/mobile layout knobs, the parallax knobs).
 *
 * DELIBERATELY DEFERRED (faithful follow-ups, flagged — same discipline as the Video section): Shrine's
 * TRANSPARENT-HEADER options + "hide announcement bars" are header-GROUP concerns (they retint the global
 * header and fit the hero under it) that a self-contained section cannot own in this architecture — they are
 * a cross-section feature (cf. named-template binding), not part of THIS section's own HTML/CSS, so they are
 * omitted here rather than shipped as dead controls.
 */
/** Convenience for the literal hex defaults Shrine ships. */
const hex = (value) => ({ ref: 'custom', value });
const ANIMATION = 'Animation';
const HEIGHT = 'Height';
const BACKGROUND = 'Background';
const PADDING = 'Section padding';
/** The parallax knobs shared by the content + image blocks (Shrine's identical "Parallax" header). */
function parallaxFields(zoomDefault, zIndexDefault) {
    return [
        {
            id: 'verticalScrollingSpeed',
            type: 'range',
            label: 'Vertical scrolling',
            group: 'Parallax',
            min: -300,
            max: 300,
            step: 10,
            unit: '%',
            default: 0,
            info: 'Vertical movement while scrolling. 0% = regular speed; negatives move up, positives down; 100% keeps the layer fixed on screen.',
        },
        {
            id: 'horizontalScrollingSpeed',
            type: 'range',
            label: 'Horizontal scrolling',
            group: 'Parallax',
            min: -300,
            max: 300,
            step: 10,
            unit: '%',
            default: 0,
            info: 'Horizontal movement while scrolling. Negatives move left, positives right; 0% = no horizontal movement.',
        },
        {
            id: 'zoomLevel',
            type: 'range',
            label: 'Zoom level',
            group: 'Parallax',
            min: 0,
            max: 400,
            step: 10,
            unit: '%',
            default: zoomDefault,
            info: 'Zoom the layer reaches at the end of the animation (once the section is scrolled past). 100% leaves it unchanged.',
        },
        {
            id: 'rotation',
            type: 'range',
            label: 'Rotation',
            group: 'Parallax',
            min: -100,
            max: 100,
            step: 2,
            unit: '%',
            default: 0,
            info: 'Rotation the layer reaches at the end of the animation. Negatives rotate left, positives right.',
        },
        {
            id: 'zIndex',
            type: 'range',
            label: 'Stacking index',
            group: 'Parallax',
            min: 0,
            max: 10,
            step: 1,
            default: zIndexDefault,
            info: 'A layer with a higher stacking index appears in front of layers with a lower one.',
        },
    ];
}
// ── Content block — Shrine's "Content" block (limit 1). ────────────────────────────────────────────────
const contentBlock = {
    type: 'content',
    name: 'Content',
    max: 1,
    fields: [
        ...parallaxFields(100, 0),
        // ── Content ──────────────────────────────────────────────────────────────
        { id: 'headingPrefix', type: 'text', label: 'Heading prefix', group: 'Content' },
        {
            id: 'heading',
            type: 'richtext',
            label: 'Heading',
            group: 'Content',
            default: { format: 'html', html: '<p>Parallax hero</p>' },
            info: 'Bold a word (B) to highlight it with the heading highlight color.',
        },
        {
            id: 'titleHighlightColor',
            type: 'color',
            label: 'Heading highlight color',
            group: 'Content',
            default: { ref: 'theme', token: 'accent' },
        },
        {
            id: 'headingSize',
            type: 'range',
            label: 'Desktop heading size',
            group: 'Content',
            min: 30,
            max: 150,
            step: 5,
            unit: 'px',
            default: 80,
        },
        {
            id: 'mobileHeadingSize',
            type: 'range',
            label: 'Mobile heading size',
            group: 'Content',
            min: 18,
            max: 90,
            step: 3,
            unit: 'px',
            default: 36,
        },
        { id: 'headingSuffix', type: 'text', label: 'Heading suffix', group: 'Content' },
        {
            id: 'headingPrefixSize',
            type: 'select',
            label: 'Heading prefix & suffix size',
            group: 'Content',
            default: 'h4',
            options: [
                { value: 'h5', label: 'Extra small' },
                { value: 'h4', label: 'Small' },
                { value: 'h3', label: 'Medium' },
                { value: 'h2', label: 'Large' },
            ],
        },
        {
            id: 'text',
            type: 'richtext',
            label: 'Text',
            group: 'Content',
            default: {
                format: 'html',
                html: '<p>Create a cool perspective scrolling effect using multiple image layers.</p>',
            },
        },
        // ── Buttons ──────────────────────────────────────────────────────────────
        {
            id: 'buttonLabel1',
            type: 'text',
            label: 'First button label',
            group: 'Content',
            default: 'Button label',
            defaultKey: 'section.button_label',
            // Placeholder de CONTENU, pas libellé d'interface — l'IA doit l'écrire (0059).
            aiWritable: true,
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'buttonLink1', type: 'link', label: 'First button link', group: 'Content' },
        {
            id: 'buttonStyleSecondary1',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'Content',
            default: false,
        },
        {
            id: 'buttonLabel2',
            type: 'text',
            label: 'Second button label',
            group: 'Content',
            info: 'Leave the label blank to hide the button.',
        },
        { id: 'buttonLink2', type: 'link', label: 'Second button link', group: 'Content' },
        {
            id: 'buttonStyleSecondary2',
            type: 'checkbox',
            label: 'Use outline button style',
            group: 'Content',
            default: false,
        },
        // ── Color scheme ─────────────────────────────────────────────────────────
        {
            id: 'colorScheme',
            type: 'select',
            label: 'Color scheme',
            group: 'Content',
            default: 'background-1',
            info: 'The custom color scheme is edited at the bottom of the block settings.',
            options: [
                { value: 'accent-1', label: 'Accent 1' },
                { value: 'accent-2', label: 'Accent 2' },
                { value: 'background-1', label: 'Background 1' },
                { value: 'background-2', label: 'Background 2' },
                { value: 'inverse', label: 'Inverse' },
                { value: 'custom', label: 'Custom' },
            ],
        },
        // ── Desktop content layout ───────────────────────────────────────────────
        {
            id: 'fullPageWidth',
            type: 'checkbox',
            label: 'Use full page width',
            group: 'Desktop content layout',
            default: false,
        },
        {
            id: 'contentMaxWidth',
            type: 'select',
            label: 'Content maximum width',
            group: 'Desktop content layout',
            default: 'unlimited',
            options: [
                { value: 'unlimited', label: 'Unlimited' },
                { value: 'pixels', label: 'Amount in pixels' },
                { value: 'percentage', label: 'Amount in percentage' },
            ],
        },
        {
            id: 'contentPixelsMaxWidth',
            type: 'number',
            label: 'Content maximum width in pixels',
            group: 'Desktop content layout',
            default: 500,
            visibleWhen: { field: 'contentMaxWidth', equals: 'pixels' },
        },
        {
            id: 'contentPercentageMaxWidth',
            type: 'range',
            label: 'Content maximum width in percentage',
            group: 'Desktop content layout',
            min: 0,
            max: 100,
            step: 5,
            unit: '%',
            default: 50,
            info: 'Relative to the container (the full page if "Use full page width" is checked). Automatically 100% on mobile.',
            visibleWhen: { field: 'contentMaxWidth', equals: 'percentage' },
        },
        {
            id: 'contentVerticalPosition',
            type: 'select',
            label: 'Content vertical position',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Top' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Bottom' },
            ],
        },
        {
            id: 'contentHorizontalPosition',
            type: 'select',
            label: 'Content horizontal position',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Right' },
            ],
        },
        {
            id: 'contentTextAlignment',
            type: 'select',
            label: 'Content text alignment',
            group: 'Desktop content layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Mobile content layout ────────────────────────────────────────────────
        {
            id: 'mobileContentVerticalPosition',
            type: 'select',
            label: 'Mobile content vertical position',
            group: 'Mobile content layout',
            default: 'center',
            options: [
                { value: 'flex-start', label: 'Top' },
                { value: 'center', label: 'Center' },
                { value: 'flex-end', label: 'Bottom' },
            ],
        },
        {
            id: 'mobileContentTextAlignment',
            type: 'select',
            label: 'Mobile content text alignment',
            group: 'Mobile content layout',
            default: 'center',
            options: [
                { value: 'left', label: 'Left' },
                { value: 'center', label: 'Center' },
                { value: 'right', label: 'Right' },
            ],
        },
        // ── Custom color scheme (applied when Color scheme = Custom) ──────────────
        {
            id: 'customColorsText',
            type: 'color',
            label: 'Text',
            group: 'Custom color scheme',
            default: hex('#2E2A39'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonBackground',
            type: 'color',
            label: 'Solid button background',
            group: 'Custom color scheme',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsSolidButtonText',
            type: 'color',
            label: 'Solid button label',
            group: 'Custom color scheme',
            default: hex('#ffffff'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
        {
            id: 'customColorsOutlineButton',
            type: 'color',
            label: 'Outline button',
            group: 'Custom color scheme',
            default: hex('#dd1d1d'),
            visibleWhen: { field: 'colorScheme', equals: 'custom' },
        },
    ],
};
// ── Image-layer block — Shrine's "Image layer" block (limit 10). ────────────────────────────────────────
const imageBlock = {
    type: 'image',
    name: 'Image layer',
    max: 10,
    fields: [
        { id: 'desktopImage', type: 'image', label: 'Desktop image', group: 'Image' },
        {
            id: 'mobileImage',
            type: 'image',
            label: 'Mobile image',
            group: 'Image',
            info: 'If empty, the desktop image is used.',
        },
        {
            id: 'hideOnMobile',
            type: 'checkbox',
            label: 'Hide on mobile',
            group: 'Image',
            default: false,
        },
        ...parallaxFields(0, 1),
    ],
};
export const parallaxHeroSchema = {
    type: 'parallax-hero',
    name: '3D Parallax hero',
    version: 1,
    templates: ['index', 'product', 'collection', 'page', 'thank-you'],
    fields: [
        // ── (top, ungrouped) ──────────────────────────────────────────────────────
        {
            id: 'visibility',
            type: 'select',
            label: 'Display on',
            default: 'always-display',
            options: [
                { value: 'always-display', label: 'All devices' },
                { value: 'mobile-hidden', label: 'Desktop only' },
                { value: 'desktop-hidden', label: 'Mobile only' },
            ],
        },
        // ── Animation ─────────────────────────────────────────────────────────────
        {
            id: 'animationStart',
            type: 'select',
            label: 'Start animation once:',
            group: ANIMATION,
            default: 'top',
            info: 'If the section is not at the very top of the page, the second option usually works better.',
            options: [
                { value: 'top', label: 'Once the top edge leaves the viewport' },
                { value: 'bottom', label: 'Once the top edge comes into the viewport' },
            ],
        },
        // ── Height ────────────────────────────────────────────────────────────────
        {
            id: 'minDesktopHeightType',
            type: 'select',
            label: 'Minimum desktop height',
            group: HEIGHT,
            default: 'pixels',
            options: [
                { value: 'autofit', label: 'None (fit content)' },
                { value: 'pixels', label: 'Amount in pixels' },
                { value: '100vh', label: 'Viewport height' },
            ],
        },
        {
            id: 'desktopPixelsHeight',
            type: 'number',
            label: 'Desktop minimum height in pixels',
            group: HEIGHT,
            default: 600,
            visibleWhen: { field: 'minDesktopHeightType', equals: 'pixels' },
        },
        {
            id: 'minMobileHeightType',
            type: 'select',
            label: 'Minimum mobile height',
            group: HEIGHT,
            default: 'pixels',
            options: [
                { value: 'autofit', label: 'None (fit content)' },
                { value: 'pixels', label: 'Amount in pixels' },
                { value: '100vh', label: 'Viewport height' },
            ],
        },
        {
            id: 'mobilePixelsHeight',
            type: 'number',
            label: 'Mobile minimum height in pixels',
            group: HEIGHT,
            default: 400,
            visibleWhen: { field: 'minMobileHeightType', equals: 'pixels' },
        },
        // ── Background ────────────────────────────────────────────────────────────
        { id: 'desktopBgImage', type: 'image', label: 'Desktop background image', group: BACKGROUND },
        {
            id: 'desktopBgVideo',
            type: 'video',
            label: 'Desktop background video',
            group: BACKGROUND,
            info: 'An uploaded clip (autoplays muted & looped). Takes priority over the desktop background image.',
        },
        { id: 'mobileBgImage', type: 'image', label: 'Mobile background image', group: BACKGROUND },
        {
            id: 'mobileBgVideo',
            type: 'video',
            label: 'Mobile background video',
            group: BACKGROUND,
            info: 'An uploaded clip (autoplays muted & looped). Takes priority over the mobile background image.',
        },
        // Une seule case pour les deux clips (ordinateur + mobile) : un seul plan, deux appareils.
        playButtonField({
            defaultOn: false,
            group: BACKGROUND,
            visibleWhen: { field: 'desktopBgVideo', empty: false },
        }),
        {
            id: 'overlayColor',
            type: 'color',
            label: 'Background overlay color',
            group: BACKGROUND,
            default: hex('#000000'),
        },
        {
            id: 'overlayOpacity',
            type: 'range',
            label: 'Background overlay opacity',
            group: BACKGROUND,
            min: 0,
            max: 100,
            step: 1,
            unit: '%',
            default: 0,
        },
        // ── Section padding ───────────────────────────────────────────────────────
        {
            id: 'paddingTop',
            type: 'range',
            label: 'Top padding',
            group: PADDING,
            min: 0,
            max: 200,
            step: 5,
            unit: 'px',
            default: 50,
        },
        {
            id: 'paddingBottom',
            type: 'range',
            label: 'Bottom padding',
            group: PADDING,
            min: 0,
            max: 200,
            step: 5,
            unit: 'px',
            default: 50,
        },
        // Text colors — the seller's own colour/gradient for this band's headings and body text.
        ...textColorFields(),
        // The universal Animation group (2026-09-18): this section writes its own section controls
        // instead of spreading `universalFields`, so the group is spread in on its own.
        ...animationFields,
    ],
    blocks: [contentBlock, imageBlock],
    maxBlocks: 11,
};
export const parallaxHeroPresets = [
    {
        name: '3D Parallax hero',
        settings: {
            visibility: 'always-display',
            animationStart: 'top',
            minDesktopHeightType: 'pixels',
            desktopPixelsHeight: 600,
            minMobileHeightType: 'pixels',
            mobilePixelsHeight: 400,
            overlayColor: hex('#000000'),
            overlayOpacity: 0,
            paddingTop: 50,
            paddingBottom: 50,
        },
        blocks: [{ type: 'content' }, { type: 'image' }, { type: 'image' }, { type: 'image' }],
    },
];
/**
 * How this section presents itself in "Add section": label, group, icon.
 *
 * It lives beside the schema, NOT in `index.ts`, and that is the point. `index.ts` is the module that pulls
 * in `Component.tsx`, and the builder reads `meta` for all sixty-two sections to paint the picker while
 * rendering only the handful a page actually uses. Keeping `meta` here lets `registry.lazy.gen.ts` import it
 * without dragging every section's render code into the editor's entry chunk.
 */
export const parallaxHeroMeta = {
    label: '3D Parallax hero',
    group: 'Layout',
    icon: 'layout',
};
//# sourceMappingURL=schema.js.map